
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { FileText, Copy, Download, Check } from 'lucide-react';
import { MortgageData } from '@/types/calculator';
import { generateMortgageTextReport, copyTextToClipboard, downloadTextFile } from '@/utils/calculator/textReportGenerator';

interface TextReportActionsProps {
  mortgageData: MortgageData;
  disabled?: boolean;
}

export const TextReportActions: React.FC<TextReportActionsProps> = ({
  mortgageData,
  disabled = false
}) => {
  const { toast } = useToast();
  const [copySuccess, setCopySuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleCopyReport = async () => {
    if (disabled) return;
    
    try {
      setIsGenerating(true);
      const reportText = generateMortgageTextReport(mortgageData);
      const success = await copyTextToClipboard(reportText);
      
      if (success) {
        setCopySuccess(true);
        toast({
          title: "Report Copied",
          description: "The mortgage report has been copied to your clipboard.",
        });
        
        setTimeout(() => setCopySuccess(false), 3000);
      } else {
        throw new Error('Clipboard operation failed');
      }
    } catch (error) {
      toast({
        title: "Copy Failed",
        description: "Couldn't copy the report. Please try the download option.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownloadReport = () => {
    if (disabled) return;
    
    try {
      setIsGenerating(true);
      const reportText = generateMortgageTextReport(mortgageData);
      const filename = `equiforge-mortgage-report-${new Date().toLocaleDateString().replace(/\//g, '-')}.txt`;
      
      downloadTextFile(reportText, filename);
      
      toast({
        title: "Report Downloaded",
        description: "Your mortgage report has been saved as a text file.",
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "There was an error downloading your report. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        onClick={handleCopyReport}
        disabled={disabled || isGenerating}
        className="flex items-center gap-2"
      >
        {copySuccess ? (
          <>
            <Check className="h-4 w-4 text-green-600" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="h-4 w-4" />
            Copy Report
          </>
        )}
      </Button>
      
      <Button
        variant="outline"
        size="sm"
        onClick={handleDownloadReport}
        disabled={disabled || isGenerating}
        className="flex items-center gap-2"
      >
        <Download className="h-4 w-4" />
        {isGenerating ? 'Generating...' : 'Download Text'}
      </Button>
    </div>
  );
};
